/**
 * Configurable Insights metric targets (status colour thresholds).
 * Defaults match SCENARIO_TARGETS / TEAM_SIZE_RANGES hardcoded values.
 * Persisted under workspace.config.insightsHierarchy.targets.
 */

import {
  MANAGER_HEALTH_THRESHOLDS,
  SCENARIO_TARGETS,
  TEAM_SIZE_RANGES,
} from "src/components/shared/TeamMetrics/constants";

const SCENARIO_KEYS = ["conservative", "standard", "aggressive"] as const;

export type ScenarioKey = (typeof SCENARIO_KEYS)[number];

/** Shared scenario Select options for Insights surfaces. */
export const SCENARIO_OPTIONS = [
  { value: "conservative" as const, label: "Conservative" },
  { value: "standard" as const, label: "Standard" },
  { value: "aggressive" as const, label: "Aggressive" },
];

export type ScenarioTargetConfig = {
  layersMax: number;
  spanMin: number;
  spanMax: number;
  /** Manager∶IC as 1:N — store N */
  managerToICDenom: number;
  managementOverheadMax: number;
  /** Percent points, e.g. 10 for 10% */
  headcountReductionPct: number;
  teamSizeMin: number;
  teamSizeMax: number;
};

export type InsightsTargetsConfig = {
  scenarios: Record<ScenarioKey, ScenarioTargetConfig>;
  managerHealthSeverelyOverloaded: number;
};

export type ScenarioTargetsMap = Record<
  ScenarioKey,
  {
    layers: { max: number };
    span: { min: number; max: number };
    managerToICRatio: { min: number };
    managementOverhead: { max: number };
    headcountReduction: { percentage: number };
    teamSize: { min: number; max: number };
  }
>;

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value === null || typeof value !== "object") return null;
  return value as Record<string, unknown>;
};

const positiveInt = (value: unknown, fallback: number): number => {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const nonNegNumber = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const denomFromRatio = (ratioMin: number): number => {
  if (!ratioMin || ratioMin <= 0) {
    return 6;
  }
  return Math.max(1, Math.round(1 / ratioMin));
};

const scenarioFromHardcoded = (key: ScenarioKey): ScenarioTargetConfig => {
  const t = SCENARIO_TARGETS[key];
  const size = TEAM_SIZE_RANGES[key];
  return {
    layersMax: t.layers.max,
    spanMin: t.span.min,
    spanMax: t.span.max,
    managerToICDenom: denomFromRatio(t.managerToICRatio.min),
    managementOverheadMax: t.managementOverhead.max,
    headcountReductionPct: Math.round(t.headcountReduction.percentage * 100),
    teamSizeMin: size.min,
    teamSizeMax: size.max,
  };
};

export const DEFAULT_INSIGHTS_TARGETS: InsightsTargetsConfig = {
  scenarios: {
    conservative: scenarioFromHardcoded("conservative"),
    standard: scenarioFromHardcoded("standard"),
    aggressive: scenarioFromHardcoded("aggressive"),
  },
  managerHealthSeverelyOverloaded:
    MANAGER_HEALTH_THRESHOLDS.SEVERELY_OVERLOADED,
};

const resolveScenario = (
  raw: unknown,
  fallback: ScenarioTargetConfig
): ScenarioTargetConfig => {
  const src = asRecord(raw) ?? {};
  const layers = asRecord(src["layers"]);
  const span = asRecord(src["span"]);
  const ratio = asRecord(src["managerToICRatio"]);
  const overhead = asRecord(src["managementOverhead"]);
  const headcount = asRecord(src["headcountReduction"]);
  const teamSize = asRecord(src["teamSize"]);

  const layersMax = positiveInt(
    src["layersMax"] ?? layers?.["max"],
    fallback.layersMax
  );
  const spanMin = positiveInt(
    src["spanMin"] ?? span?.["min"],
    fallback.spanMin
  );
  const spanMax = positiveInt(
    src["spanMax"] ?? span?.["max"],
    fallback.spanMax
  );
  let managerToICDenom = positiveInt(
    src["managerToICDenom"],
    fallback.managerToICDenom
  );
  if (src["managerToICDenom"] == null && ratio?.["min"] != null) {
    managerToICDenom = denomFromRatio(Number(ratio["min"]));
  }
  const managementOverheadMax = nonNegNumber(
    src["managementOverheadMax"] ?? overhead?.["max"],
    fallback.managementOverheadMax
  );
  let headcountReductionPct = nonNegNumber(
    src["headcountReductionPct"],
    fallback.headcountReductionPct
  );
  if (
    src["headcountReductionPct"] == null &&
    headcount?.["percentage"] != null
  ) {
    const pct = Number(headcount["percentage"]);
    headcountReductionPct = pct <= 1 ? Math.round(pct * 100) : pct;
  }
  const teamSizeMin = positiveInt(
    src["teamSizeMin"] ?? teamSize?.["min"],
    fallback.teamSizeMin
  );
  const teamSizeMax = positiveInt(
    src["teamSizeMax"] ?? teamSize?.["max"],
    fallback.teamSizeMax
  );

  return {
    layersMax,
    spanMin: Math.min(spanMin, spanMax),
    spanMax: Math.max(spanMin, spanMax),
    managerToICDenom,
    managementOverheadMax,
    headcountReductionPct,
    teamSizeMin: Math.min(teamSizeMin, teamSizeMax),
    teamSizeMax: Math.max(teamSizeMin, teamSizeMax),
  };
};

export const resolveInsightsTargets = (raw: unknown): InsightsTargetsConfig => {
  const src = asRecord(raw) ?? {};
  const scenariosRaw = asRecord(src["scenarios"]) ?? src;

  const scenarios = {} as Record<ScenarioKey, ScenarioTargetConfig>;
  for (const key of SCENARIO_KEYS) {
    scenarios[key] = resolveScenario(
      scenariosRaw[key],
      DEFAULT_INSIGHTS_TARGETS.scenarios[key]
    );
  }

  return {
    scenarios,
    managerHealthSeverelyOverloaded: positiveInt(
      src["managerHealthSeverelyOverloaded"],
      DEFAULT_INSIGHTS_TARGETS.managerHealthSeverelyOverloaded
    ),
  };
};

export const toScenarioTargetsMap = (
  config: InsightsTargetsConfig
): ScenarioTargetsMap => {
  const out = {} as ScenarioTargetsMap;
  for (const key of SCENARIO_KEYS) {
    const s = config.scenarios[key];
    out[key] = {
      layers: { max: s.layersMax },
      span: { min: s.spanMin, max: s.spanMax },
      managerToICRatio: { min: 1 / s.managerToICDenom },
      managementOverhead: { max: s.managementOverheadMax },
      headcountReduction: { percentage: s.headcountReductionPct / 100 },
      teamSize: { min: s.teamSizeMin, max: s.teamSizeMax },
    };
  }
  return out;
};

export const toPersistedInsightsTargets = (
  config: InsightsTargetsConfig
): InsightsTargetsConfig => ({
  scenarios: { ...config.scenarios },
  managerHealthSeverelyOverloaded: config.managerHealthSeverelyOverloaded,
});

export const METRIC_HELP = {
  layers: [
    "Reporting depth from the configured top of the hierarchy (supply apex by default).",
    "Num = this team’s layer number under Layers; Avg/Max = people in this population. Origin shows whether numbering starts at 0 or 1.",
  ],
  span: [
    "Direct reports per manager in the active formal hierarchy (team owners or people managers).",
    "Not the same as the IC∶manager ratio.",
  ],
  managerToIC: [
    "Individual contributors per manager in this population (ICs ÷ managers).",
    "Based on who has direct reports — not job title. Distinct from average span.",
  ],
  managementOverhead: [
    "Share of the population who are managers (managers ÷ headcount).",
    "Lower means fewer people-leadership seats relative to headcount.",
  ],
  memberCount: [
    "Design capacity: people allocated to this team and sub-teams, including designed vacant seats; open requisitions and AI agents are excluded.",
  ],
  memberFte: [
    "Sum of FTE for the same people as headcount (design-capacity population).",
  ],
} as const;
